import type { MessageAttachment } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { IndividualsDuplexerChannel } from '@wix/ai-scheduling-assistant-common-lib/commonTypes';
import { useDuplexerSubscription } from './use-duplexer-subscription';

/**
 * Subscribes to the assistant-chat duplexer channel. The harness publishes the user
 * chat's ASSISTANT / THINKING / ASSISTANT_STEP rows to ASSISTANT_CHAT, AND a worker
 * agent's own conversation (BRAIN source) as ASSISTANT + TOOL_USE rows for the live
 * "View Agents Conversation" transcript (see AssistantDuplexerService / the message
 * consumer). For each event we hand the raw entity to onEntity; the caller decides
 * whether it belongs to the conversation it's showing and whether it's a duplicate.
 * A pushed worker TOOL_USE row carries a server-computed `actionLine` with `toolInput`
 * cleared, so the raw tool call never reaches the browser.
 */
export interface DuplexerEntity {
  id?: string;
  conversationId?: string;
  role?: string;
  content?: string;
  toolName?: string;
  toolInput?: string;
  // Server-computed human action line for a worker TOOL_USE row (raw args stripped).
  actionLine?: string;
  createdDate?: string;
  attachments?: MessageAttachment[];
  metadata?: {
    attachments?: MessageAttachment[];
    // "Agent joined your team" marker — the harness stamps this on the ASSISTANT
    // row it persists when the Maestro hires an agent. Read via `readAgentCard`.
    agentCard?: { name?: string; title?: string };
    [key: string]: unknown;
  };
}

/**
 * Attachments off a duplexer push. The typed field only — the backfill has migrated every
 * historical row, so there is no legacy copy left to fall back to.
 */
export function duplexerAttachments(entity: DuplexerEntity): MessageAttachment[] | undefined {
  return entity.attachments && entity.attachments.length > 0 ? entity.attachments : undefined;
}

export function useDuplexer(onEntity: (entity: DuplexerEntity) => void) {
  useDuplexerSubscription<DuplexerEntity>(IndividualsDuplexerChannel.ASSISTANT_CHAT, (payload) => {
    if (payload.entity) onEntity(payload.entity);
  });
}
